import { useEffect } from 'react'
import { ApolloClient } from '@apollo/client'
import { setCookie } from 'cookies-next'
import { ParsedUrlQuery } from 'querystring'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { ServerExperiments } from '@/experimentation'
import { runExperimentsOnServer } from '@/experimentation/server'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { useLegacyEffect } from '@/molecules/utils'
import { ValidSale } from '@/services/ContentfulSaleService'
import { getGuildPromotion, Promotion } from '@/services/GuildPromotionService'
import { GetGuildTheatricalReleasesResult } from '@/services/GuildReleasesService/TheatricalReleaseService'
import {
  getMarketingOfferPageBySlug,
  GuildEarlyAccessSectionModel,
  GuildMarketingOfferPageData,
} from '@/services/GuildSignupPageService'
import {
  GuildProjectSlugs,
  GuildSignupEmphasis,
  GuildSignupLayout,
} from '@/services/GuildSignupPageService/GuildSignupPageTypes'
import { isGuildSignupSimpleLayout } from '@/services/GuildSignupPageService/validations'
import { GuildSale, transformContentfulSaleToValidatedSale } from '@/services/SaleService'
import { GuildSignupExplainerLayout, GuildTestimonialSection } from '@/types/codegen-contentful'
import { useSafeTrack } from '@/utils/analytics'
import { useStickyAttribution } from '@/utils/analytics/StickyAttribution'
import { projectSlugsAttribution } from '@/utils/analytics/attribution'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { getUserGuildStatus, GuildStatus } from '@/utils/users/usersServer'
import { GuildMovieTicketRelease } from '@/views/GuildDashboardView/GuildMovieTicketsPanel/types'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'
import { getGuildApprovedTorchPosters } from '@/views/GuildSignupView/GuildSignupContext/GuildSignupContext'
import { MarketingWinbackView } from '@/views/MarketingWinbackView'

interface GuildMarketingOfferProps {
  emphasis: GuildSignupEmphasis
  guildMemberCta: string
  guildMemberHref: string
  guildApprovedTorchPosters: string[]
  guildExplainerLayout?: GuildSignupExplainerLayout | null
  layout: GuildSignupLayout
  locale: string
  projectSlugs?: GuildProjectSlugs
  promotion: Promotion | null
  sale: GuildSale | null
  slug: string
  theatricalReleases: GetGuildTheatricalReleasesResult
  guildTestimonialSection?: GuildTestimonialSection
  guildEarlyAccessSection?: GuildEarlyAccessSectionModel
  movieTicketReleases: GuildMovieTicketRelease[]
  hasUser: boolean
  logo?: {
    src: string
    alt: string
    className?: string
  }
  experiments?: ServerExperiments
  region: string
}

export const GuildMarketingOfferPage: NextPageWithLayout<GuildMarketingOfferProps> = ({
  emphasis,
  guildMemberCta,
  guildMemberHref,
  guildApprovedTorchPosters,
  guildExplainerLayout,
  layout,
  locale,
  projectSlugs,
  promotion,
  sale,
  slug,
  theatricalReleases,
  guildTestimonialSection,
  guildEarlyAccessSection,
  movieTicketReleases,
  logo,
  experiments,
  region,
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()

  useLegacyEffect(() => {
    track('Guild Product Page Viewed', { pageContext: emphasis, slug })
    setCookie('guildFocus', slug, { maxAge: Durations.ONE_YEAR_IN_SECONDS })
  }, [track, slug, emphasis])

  useEffect(() => {
    writeToSessionStorage('pageSlug', slug)
    projectSlugsAttribution(projectSlugs)
  }, [slug, projectSlugs])

  useStickyAttribution('guild-attribution')

  return (
    <div className={isGuildSignupSimpleLayout(layout) ? 'bg-white' : 'bg-core-gray-950'}>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1717006540/open-graph-240228.jpg',
          cloudinaryTransformation: 'b_rgb:000000',
          description: t(
            'angelStudiosGuildJoinInfoMetaDescriptionV2',
            'As an Angel Guild member, you can vote on upcoming shows, receive free tickets to every theatrical release, gain early access to shows, receive merchandise discounts, and continue to amplify light.',
          ),
          locale,
          path: `${paths.guild.join}/${slug}`,
          title: t('angelStudiosGuildEarlyAccessMetaTitleV2', 'Angel Guild Membership | Angel Studios'),
        })}
      />
      <GuildSignupContextProvider
        guildTestimonialSection={guildTestimonialSection}
        guildEarlyAccessSection={guildEarlyAccessSection}
        emphasis={emphasis}
        guildApprovedTorchPosters={guildApprovedTorchPosters}
        guildExplainerLayout={guildExplainerLayout}
        guildMemberCta={guildMemberCta}
        guildMemberHref={guildMemberHref}
        layout={layout}
        projectSlugs={projectSlugs}
        promotion={promotion}
        sale={sale}
        slug={slug}
        theatricalReleases={theatricalReleases}
        movieTicketReleases={movieTicketReleases}
        logo={logo}
        experiments={experiments}
        region={region}
      >
        <MarketingWinbackView />
      </GuildSignupContextProvider>
    </div>
  )
}

function shouldRedirectToDashboard(guildStatus: GuildStatus | null, emphasis: string) {
  if (!guildStatus) return false
  return (
    guildStatus &&
    ((emphasis === 'tickets' && guildStatus.guildRoles?.hasFreeTickets) ||
      (emphasis === 'early-access' && guildStatus.guildRoles?.hasEarlyAccess))
  )
}

function getDestinationUrl(
  pageData: GuildMarketingOfferPageData,
  emphasis: GuildSignupEmphasis,
  query: ParsedUrlQuery,
) {
  let destination = pageData.guildMemberHref ?? `/guild#${emphasis}`
  if (query['tv-post-auth'] === 'True') {
    const hasQuery = destination.includes('?')
    destination = `${destination}${hasQuery ? '&' : '?'}tv-post-auth=True`
  }
  return destination
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, preview, query, userId, params, req, res, region }) => {
    const slug = getItemOrFirstEntry(params?.slug)
    const isSupportLink = query.special === 'last-chance'

    if (!slug) {
      return { redirect: { destination: paths.guild.join, permanent: false } }
    }

    const [pageData, guildStatus] = await Promise.all([
      getMarketingOfferPageBySlug({ slug, opts: { locale, preview } }),
      getUserGuildStatus(userId),
    ])

    if (!pageData) {
      return {
        redirect: {
          destination: paths.guild.join,
          permanent: false,
        },
      }
    }

    if (pageData.redirectTo) {
      return {
        redirect: {
          destination: pageData.redirectTo,
          permanent: false,
        },
      }
    }

    const emphasis = pageData.emphasis || 'early-access'
    if (shouldRedirectToDashboard(guildStatus, emphasis)) {
      return {
        redirect: {
          destination: getDestinationUrl(pageData, emphasis, query),
          permanent: false,
        },
      }
    }

    const layout = pageData.layout
    const guildExplainerLayout = {
      ...pageData.explainerLayout,
      title: 'Continue this incredible journey with us.',
    }

    const [guildApprovedTorchPosters, promotion, sale, experiments, translations] = await Promise.all([
      getGuildApprovedTorchPosters(),
      getGuildPromotion({ opts: { locale, preview }, isSupportLink, federationClient }),
      getOffer(federationClient, pageData?.sale),
      runExperimentsOnServer(req, res, [
        { experimentKey: 'guild_special_offer_stacked_plan_cards', defaultValue: false },
      ]),
      loadTranslations(locale, ['common', 'join-the-guild', 'guild', 'guild-benefits', 'app-promo']),
    ])

    return {
      props: {
        emphasis,
        guildApprovedTorchPosters,
        guildExplainerLayout,
        guildMemberCta: pageData.guildMemberCta,
        guildMemberHref: pageData.guildMemberHref,
        layout,
        locale,
        projectSlugs: pageData.projectSlugs,
        promotion,
        sale,
        slug,
        hasUser: Boolean(userId),
        experiments,
        isDarkMode: layout?.__typename !== 'GuildSignupSimpleLayout',
        region,
        ...translations,
      },
    }
  },
)

GuildMarketingOfferPage.getLayout = (page, pageProps) => {
  return <DefaultLayout isDarkMode={Boolean(pageProps?.isDarkMode)}>{page}</DefaultLayout>
}

export default GuildMarketingOfferPage

async function getOffer(federationClient: ApolloClient<object>, sale: ValidSale) {
  return await transformContentfulSaleToValidatedSale(sale, federationClient)
}
